import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminEmailSettingsService } from '../../../services/adminEmailSettingsService';
import { Save, Send, CheckCircle, Eye, EyeOff } from 'lucide-react';
import { Button, SearchSelect } from '../../shared';
import { useToastContext } from '../../../contexts/ToastContext';

const EmailSettings = () => {
  const queryClient = useQueryClient();
  const toast = useToastContext();
  const [showApiToken, setShowApiToken] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [isTesting, setIsTesting] = useState(false);

  const [formData, setFormData] = useState({
    mailtrap_api_token: '',
    mailtrap_inbox_id: '',
    mailtrap_mode: 'sandbox',
    from_email: '',
    from_email_local: '',
    from_email_domain: '',
    from_name: '',
    is_active: false,
    send_to_vendor_on_booking: true,
    send_to_client_on_booking: true,
    send_to_admin_on_booking: true,
    send_to_vendor_on_payment: true,
    send_to_client_on_payment: true,
    send_to_admin_on_payment: true,
    send_password_reset: true,
    send_welcome_email: true,
    send_booking_confirmation: true,
    send_payment_confirmation: true,
  });

  // Fetch email settings
  const { data: settingsResponse, isLoading } = useQuery({
    queryKey: ['adminEmailSettings'],
    queryFn: () => adminEmailSettingsService.get(),
  });

  // Fetch verified domains when in transactional mode
  const { data: domainsResponse, isLoading: isLoadingDomains, refetch: refetchDomains } = useQuery({
    queryKey: ['mailtrapDomains', formData.mailtrap_mode, formData.mailtrap_api_token],
    queryFn: () => adminEmailSettingsService.getDomains(),
    enabled: formData.mailtrap_mode === 'transactional' && !!formData.mailtrap_api_token,
    retry: false,
  });

  const domains = React.useMemo(() => domainsResponse?.data || [], [domainsResponse?.data]);

  React.useEffect(() => {
    if (settingsResponse?.data) {
      const settings = settingsResponse.data;
      // Parse from_email for transactional mode
      const fromEmail = settings.from_email || '';
      let fromEmailLocal = '';
      let fromEmailDomain = '';
      
      if (settings.mailtrap_mode === 'transactional' && fromEmail.includes('@')) {
        const parts = fromEmail.split('@');
        fromEmailLocal = parts[0] || '';
        fromEmailDomain = parts[1] || '';
        
        // Warn if domain doesn't match verified domains
        if (domains.length > 0 && fromEmailDomain) {
          const domainNames = domains.map(d => d.name);
          if (!domainNames.includes(fromEmailDomain)) {
            const commonEmailProviders = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com'];
            if (commonEmailProviders.includes(fromEmailDomain.toLowerCase())) {
              toast.error(null, `⚠️ Your current From Email uses "${fromEmailDomain}" which is not allowed in Transactional Stream mode. Please use a verified domain like ${domainNames[0] || 'vendalist.com'}. Example: hello@${domainNames[0] || 'vendalist.com'}`);
            } else {
              toast.error(null, `⚠️ Your current From Email domain "${fromEmailDomain}" is not in your verified domains list. Please use: ${domainNames.join(', ')}. Example: hello@${domainNames[0] || 'vendalist.com'}`);
            }
          }
        }
      }

      setFormData({
        mailtrap_api_token: settings.mailtrap_api_token || '',
        mailtrap_inbox_id: settings.mailtrap_inbox_id || '',
        mailtrap_mode: settings.mailtrap_mode || 'sandbox',
        from_email: fromEmail,
        from_email_local: fromEmailLocal,
        from_email_domain: fromEmailDomain,
        from_name: settings.from_name || '',
        is_active: settings.is_active ?? false,
        send_to_vendor_on_booking: settings.send_to_vendor_on_booking ?? true,
        send_to_client_on_booking: settings.send_to_client_on_booking ?? true,
        send_to_admin_on_booking: settings.send_to_admin_on_booking ?? true,
        send_to_vendor_on_payment: settings.send_to_vendor_on_payment ?? true,
        send_to_client_on_payment: settings.send_to_client_on_payment ?? true,
        send_to_admin_on_payment: settings.send_to_admin_on_payment ?? true,
        send_password_reset: settings.send_password_reset ?? true,
        send_welcome_email: settings.send_welcome_email ?? true,
        send_booking_confirmation: settings.send_booking_confirmation ?? true,
        send_payment_confirmation: settings.send_payment_confirmation ?? true,
      });
    }
  }, [settingsResponse, domains, toast]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: (data) => adminEmailSettingsService.save(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['adminEmailSettings']);
      toast.success(null, 'Email settings saved successfully');
    },
    onError: (error) => {
      toast.error(null, error.response?.data?.message || 'Failed to save email settings');
    },
  });

  // Test email mutation
  const testMutation = useMutation({
    mutationFn: (email) => adminEmailSettingsService.test(email),
    onSuccess: () => {
      toast.success(null, 'Test email sent successfully!');
      setTestEmail('');
    },
    onError: (error) => {
      toast.error(null, error.response?.data?.message || 'Failed to send test email');
    },
    onSettled: () => {
      setIsTesting(false);
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Combine local and domain for transactional mode
    const dataToSave = { ...formData };
    if (formData.mailtrap_mode === 'transactional' && formData.from_email_local && formData.from_email_domain) {
      dataToSave.from_email = `${formData.from_email_local}@${formData.from_email_domain}`;
    }
    
    // Validate email format
    if (formData.mailtrap_mode === 'transactional') {
      if (!formData.from_email_local || !formData.from_email_domain) {
        toast.error(null, 'Please enter both the local part and domain for the From Email');
        return;
      }
      const fullEmail = `${formData.from_email_local}@${formData.from_email_domain}`;
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(fullEmail)) {
        toast.error(null, 'Please enter a valid email address');
        return;
      }
      
      // Validate that the domain is from verified domains list
      if (domains.length > 0) {
        const domainNames = domains.map(d => d.name);
        if (!domainNames.includes(formData.from_email_domain)) {
          toast.error(null, `The domain "${formData.from_email_domain}" is not in your verified domains list. Please select from: ${domainNames.join(', ')}`);
          return;
        }
      }
      
      // Warn about common email provider domains
      const commonEmailProviders = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com'];
      if (commonEmailProviders.includes(formData.from_email_domain.toLowerCase())) {
        toast.error(null, `You cannot use "${formData.from_email_domain}" in Transactional Stream mode. You must use a verified domain from Mailtrap (e.g., vendalist.com).`);
        return;
      }
      
      dataToSave.from_email = fullEmail;
    }
    
    saveMutation.mutate(dataToSave);
  };

  const handleTestEmail = () => {
    if (!testEmail || !testEmail.includes('@')) {
      toast.error(null, 'Please enter a valid email address');
      return;
    }
    setIsTesting(true);
    testMutation.mutate(testEmail);
  };

  const handleToggle = (field) => {
    setFormData(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading email settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Email Settings</h2>
            <p className="mt-1 text-sm text-gray-600">
              Configure Mailtrap integration and email preferences
            </p>
          </div>
          {formData.is_active && (
            <span className="px-3 py-1 text-sm font-medium bg-green-100 text-green-800 rounded-full flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              Active
            </span>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        {/* Mailtrap Configuration */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Mailtrap Configuration</h3>
          
          <div>
            <SearchSelect
              label="Mailtrap Mode"
              required
              options={[
                { value: 'sandbox', label: 'Email Testing (Sandbox) - For testing emails' },
                { value: 'transactional', label: 'Transactional Stream (Sending Domain) - For production emails' }
              ]}
              value={formData.mailtrap_mode}
              onChange={(value) => {
                // Reset email fields when switching modes
                if (value === 'transactional') {
                  setFormData({ 
                    ...formData, 
                    mailtrap_mode: value,
                    from_email_local: '',
                    from_email_domain: '',
                    from_email: ''
                  });
                } else {
                  setFormData({ 
                    ...formData, 
                    mailtrap_mode: value,
                    from_email_local: '',
                    from_email_domain: ''
                  });
                }
              }}
              placeholder="Select Mailtrap mode"
              searchable={false}
              className="w-full"
            />
            <p className="mt-1 text-xs text-gray-500">
              {formData.mailtrap_mode === 'sandbox' 
                ? 'Use Sandbox mode for testing. Requires an inbox ID.'
                : 'Use Transactional Stream for production. No inbox ID needed.'}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mailtrap API Token *
            </label>
            <div className="relative">
              <input
                type={showApiToken ? 'text' : 'password'}
                required
                value={formData.mailtrap_api_token}
                onChange={(e) => setFormData({ ...formData, mailtrap_api_token: e.target.value })}
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                placeholder="Enter your Mailtrap API token"
              />
              <button
                type="button"
                onClick={() => setShowApiToken(!showApiToken)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showApiToken ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Get your API token from Mailtrap dashboard (Settings → API Tokens). 
              {formData.mailtrap_mode === 'transactional' 
                ? ' For Transactional Stream, use a token with "Send Email" permissions.'
                : ' For Sandbox, use a token that matches your inbox.'}
            </p>
          </div>

          {formData.mailtrap_mode === 'sandbox' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mailtrap Inbox ID *
              </label>
              <input
                type="number"
                required={formData.mailtrap_mode === 'sandbox'}
                value={formData.mailtrap_inbox_id}
                onChange={(e) => setFormData({ ...formData, mailtrap_inbox_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                placeholder="Enter your Mailtrap inbox ID"
              />
            <p className="mt-1 text-xs text-gray-500">
              Find your inbox ID in Mailtrap inbox settings. Make sure the API token has access to this inbox.
            </p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                From Email *
              </label>
              {formData.mailtrap_mode === 'transactional' ? (
                <div className="flex gap-2">
                  <input
                    type="text"
                    required
                    value={formData.from_email_local || ''}
                    onChange={(e) => {
                      let local = e.target.value.trim();
                      
                      // Remove @ symbol if user accidentally includes it
                      local = local.replace(/@+/g, '');
                      
                      // If user pastes a full email, extract just the local part
                      if (local.includes('@')) {
                        const parts = local.split('@');
                        local = parts[0];
                        // If domain is empty, set it from the pasted email
                        if (!formData.from_email_domain && parts[1]) {
                          setFormData({ 
                            ...formData, 
                            from_email_local: local,
                            from_email_domain: parts[1],
                            from_email: `${local}@${parts[1]}`
                          });
                          return;
                        }
                      }
                      
                      // Remove spaces
                      local = local.replace(/\s+/g, '');
                      
                      const domain = formData.from_email_domain || '';
                      setFormData({ 
                        ...formData, 
                        from_email_local: local,
                        from_email: domain ? `${local}@${domain}` : local
                      });
                    }}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                    placeholder="hello"
                    title="Enter local part only (e.g., hello, noreply, info), not a full email address"
                  />
                  <span className="flex items-center text-gray-500">@</span>
                  <div className="flex-1 flex gap-2">
                    {domains.length > 0 ? (
                      <SearchSelect
                        value={formData.from_email_domain || ''}
                        onChange={(value) => {
                          const local = formData.from_email_local || '';
                          setFormData({ 
                            ...formData, 
                            from_email_domain: value,
                            from_email: local ? `${local}@${value}` : value
                          });
                        }}
                        options={domains.map(domain => ({
                          value: domain.name,
                          label: domain.name
                        }))}
                        placeholder="Select domain"
                        searchable={true}
                        className="flex-1"
                      />
                    ) : (
                      <input
                        type="text"
                        required
                        value={formData.from_email_domain || ''}
                        onChange={(e) => {
                          let domain = e.target.value.trim();
                          
                          // Remove @ symbol if user accidentally includes it at the start
                          domain = domain.replace(/^@+/, '');
                          
                          // If user pastes a full email, extract just the domain part
                          if (domain.includes('@')) {
                            const parts = domain.split('@');
                            if (parts.length === 2) {
                              // If local part is empty, use the part before @
                              if (!formData.from_email_local && parts[0]) {
                                setFormData({ 
                                  ...formData, 
                                  from_email_local: parts[0],
                                  from_email_domain: parts[1],
                                  from_email: `${parts[0]}@${parts[1]}`
                                });
                                return;
                              } else {
                                // Just extract domain (last part after @)
                                domain = parts[parts.length - 1];
                              }
                            } else {
                              // Multiple @ symbols, get the last part
                              domain = parts[parts.length - 1];
                            }
                          }
                          
                          // Remove any spaces and invalid characters
                          domain = domain.replace(/\s+/g, '').toLowerCase();
                          
                          // Remove any trailing dots or invalid characters
                          domain = domain.replace(/\.+$/, '');
                          
                          const local = formData.from_email_local || '';
                          setFormData({ 
                            ...formData, 
                            from_email_domain: domain,
                            from_email: local ? `${local}@${domain}` : domain
                          });
                        }}
                        onBlur={(e) => {
                          // Validate domain format on blur
                          const domain = e.target.value.trim();
                          if (domain) {
                            // Domain validation regex
                            const domainRegex = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;
                            if (!domainRegex.test(domain)) {
                              toast.error(null, 'Please enter a valid domain name (e.g., vendalist.com) without @ symbol or email address');
                            }
                          }
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                        placeholder="vendalist.com"
                        pattern="[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}"
                        title="Enter domain only (e.g., vendalist.com), not a full email address"
                      />
                    )}
                    {formData.mailtrap_mode === 'transactional' && formData.mailtrap_api_token && (
                      <button
                        type="button"
                        onClick={() => refetchDomains()}
                        disabled={isLoadingDomains}
                        className="px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Refresh domains"
                      >
                        {isLoadingDomains ? '...' : '↻'}
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <input
                  type="email"
                  required
                  value={formData.from_email}
                  onChange={(e) => setFormData({ ...formData, from_email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                  placeholder="noreply@example.com"
                />
              )}
              {formData.mailtrap_mode === 'transactional' && (
                <div className="mt-1">
                  {isLoadingDomains && (
                    <p className="text-xs text-gray-500">Loading verified domains...</p>
                  )}
                  {!isLoadingDomains && domains.length > 0 && (
                    <p className="text-xs text-green-600">
                      ✓ Found {domains.length} verified domain{domains.length !== 1 ? 's' : ''}
                    </p>
                  )}
                  {!isLoadingDomains && domains.length === 0 && formData.mailtrap_api_token && (
                    <p className="text-xs text-amber-600">
                      ⚠️ Could not fetch domains automatically. Please enter your verified domain manually (e.g., vendalist.com)
                    </p>
                  )}
                  {!formData.mailtrap_api_token && (
                    <p className="text-xs text-amber-600">
                      ⚠️ Domain must be verified in Mailtrap (e.g., vendalist.com)
                    </p>
                  )}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                From Name *
              </label>
              <input
                type="text"
                required
                value={formData.from_name}
                onChange={(e) => setFormData({ ...formData, from_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                placeholder="Your Company Name"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              className="w-4 h-4 text-black border-gray-300 rounded focus:ring-black"
            />
            <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
              Enable Email Service
            </label>
          </div>
        </div>

        {/* Email Preferences */}
        <div className="space-y-4 border-t border-gray-200 pt-6">
          <h3 className="text-lg font-semibold text-gray-900">Email Preferences</h3>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <label className="text-sm font-medium text-gray-900">Send emails to vendors on booking</label>
                <p className="text-xs text-gray-500">Vendors receive email when a new booking is placed</p>
              </div>
              <input
                type="checkbox"
                checked={formData.send_to_vendor_on_booking}
                onChange={() => handleToggle('send_to_vendor_on_booking')}
                className="w-4 h-4 text-black border-gray-300 rounded focus:ring-black"
              />
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <label className="text-sm font-medium text-gray-900">Send emails to clients on booking</label>
                <p className="text-xs text-gray-500">Clients receive email when their booking is confirmed</p>
              </div>
              <input
                type="checkbox"
                checked={formData.send_to_client_on_booking}
                onChange={() => handleToggle('send_to_client_on_booking')}
                className="w-4 h-4 text-black border-gray-300 rounded focus:ring-black"
              />
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <label className="text-sm font-medium text-gray-900">Send emails to admin on booking</label>
                <p className="text-xs text-gray-500">Admins receive email notifications for new bookings</p>
              </div>
              <input
                type="checkbox"
                checked={formData.send_to_admin_on_booking}
                onChange={() => handleToggle('send_to_admin_on_booking')}
                className="w-4 h-4 text-black border-gray-300 rounded focus:ring-black"
              />
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <label className="text-sm font-medium text-gray-900">Send emails to vendors on payment</label>
                <p className="text-xs text-gray-500">Vendors receive email when payment is received</p>
              </div>
              <input
                type="checkbox"
                checked={formData.send_to_vendor_on_payment}
                onChange={() => handleToggle('send_to_vendor_on_payment')}
                className="w-4 h-4 text-black border-gray-300 rounded focus:ring-black"
              />
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <label className="text-sm font-medium text-gray-900">Send emails to clients on payment</label>
                <p className="text-xs text-gray-500">Clients receive email confirmation when payment is made</p>
              </div>
              <input
                type="checkbox"
                checked={formData.send_to_client_on_payment}
                onChange={() => handleToggle('send_to_client_on_payment')}
                className="w-4 h-4 text-black border-gray-300 rounded focus:ring-black"
              />
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <label className="text-sm font-medium text-gray-900">Send emails to admin on payment</label>
                <p className="text-xs text-gray-500">Admins receive email notifications for payments</p>
              </div>
              <input
                type="checkbox"
                checked={formData.send_to_admin_on_payment}
                onChange={() => handleToggle('send_to_admin_on_payment')}
                className="w-4 h-4 text-black border-gray-300 rounded focus:ring-black"
              />
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <label className="text-sm font-medium text-gray-900">Send password reset emails</label>
                <p className="text-xs text-gray-500">Users receive email when they request password reset</p>
              </div>
              <input
                type="checkbox"
                checked={formData.send_password_reset}
                onChange={() => handleToggle('send_password_reset')}
                className="w-4 h-4 text-black border-gray-300 rounded focus:ring-black"
              />
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <label className="text-sm font-medium text-gray-900">Send welcome emails</label>
                <p className="text-xs text-gray-500">New users receive welcome email upon registration</p>
              </div>
              <input
                type="checkbox"
                checked={formData.send_welcome_email}
                onChange={() => handleToggle('send_welcome_email')}
                className="w-4 h-4 text-black border-gray-300 rounded focus:ring-black"
              />
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <label className="text-sm font-medium text-gray-900">Send booking confirmation emails</label>
                <p className="text-xs text-gray-500">Send confirmation emails for bookings</p>
              </div>
              <input
                type="checkbox"
                checked={formData.send_booking_confirmation}
                onChange={() => handleToggle('send_booking_confirmation')}
                className="w-4 h-4 text-black border-gray-300 rounded focus:ring-black"
              />
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <label className="text-sm font-medium text-gray-900">Send payment confirmation emails</label>
                <p className="text-xs text-gray-500">Send confirmation emails for payments</p>
              </div>
              <input
                type="checkbox"
                checked={formData.send_payment_confirmation}
                onChange={() => handleToggle('send_payment_confirmation')}
                className="w-4 h-4 text-black border-gray-300 rounded focus:ring-black"
              />
            </div>
          </div>
        </div>

        {/* Test Email */}
        <div className="space-y-4 border-t border-gray-200 pt-6">
          <h3 className="text-lg font-semibold text-gray-900">Test Email Configuration</h3>
          <div className="flex gap-3">
            <input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="Enter email address to test"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
            />
            <Button
              type="button"
              variant="outline"
              icon={Send}
              onClick={handleTestEmail}
              disabled={isTesting || !testEmail}
              loading={isTesting}
            >
              Send Test Email
            </Button>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
          <Button
            type="submit"
            variant="primary"
            icon={Save}
            loading={saveMutation.isPending}
          >
            Save Settings
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EmailSettings;

